<script lang="ts" setup>
import api from "@/api";
import type { TEditSchoolParams } from "@/api/types";
import { SimplifiedDate } from "@/assets/js/dateHelper";
import {
	TAG_TYPES,
	type TSchoolSuperAdminView,
	type TSubscription,
	type TTag,
} from "@/assets/js/globalTypes";
import { setupChangesFields, useAsyncState, useCan } from "@/assets/js/helpers";
import { openConfirmDeleteModal } from "@/components/modals/modalDefinitions";
import { useToastStore } from "@/store/toast";

defineOptions({
	name: "admin.schools.details",
});
definePage({
	name: "admin.schools.details",
	meta: {
		hasKeepAlive: ["admin.schools"],
	},
});

const can = useCan();
const route = useRoute("admin.schools.details");
const router = useRouter();
const schoolId = computed(() => Number(route.params.id));
const schoolInfo = ref<Partial<TSchoolSuperAdminView>>(null);
const schoolChanges = ref<Partial<TSchoolSuperAdminView>>({});

const schoolNatures = ref<TTag[]>([]);

const changed = ref(false);
watch(
	schoolChanges,
	() => {
		changed.value = true;
	},
	{ deep: true, immediate: false },
);

const subscription = ref<TSubscription>();

const [getSchoolInfo] = useAsyncState(async () => {
	try {
		const res = await api.getSchoolInfo(schoolId.value);
		if (res.data.data.subscription) {
			res.data.data.subscription = {
				...res.data.data.subscription,
				sbr_start: SimplifiedDate.fromUtcToLocal(
					res.data.data.subscription.sbr_start,
				),
				sbr_end: SimplifiedDate.fromUtcToLocal(
					res.data.data.subscription.sbr_end,
				),
			};
		}
		schoolInfo.value = res.data.data;
		setupFields();
	} catch (e) {
		router.push({
			name: "admin.schools",
		});
		toastStore.openToastError(e.response.data.message);
	}
});

const tagSearch = ref("");
const [getNatures] = useAsyncState(async () => {
	const res = await api.getAllNatures({
		tag_name: tagSearch.value,
		per_page: 9999,
	});
	schoolNatures.value = res.data.data;
	return res;
});

function setupFields() {
	setupChangesFields(schoolInfo.value, schoolChanges);
	subscription.value = schoolInfo.value.subscription
		? { ...schoolInfo.value.subscription }
		: null;
	nextTick(() => {
		changed.value = false;
	});
}

function addEmptySubscription() {
	subscription.value = {
		sbr_id: 0,
		scl_id: schoolId.value,
		sbr_price: null,
		sbr_start: null,
		sbr_end: null,
		sbr_status: "Active",
		sbr_created: "",
		sbr_updated: "",
		sbr_deleted: "",
	};
}

function openDeleteModal() {
	openConfirmDeleteModal({
		name: schoolInfo.value.scl_name,
		confirmDelete: false,
		data: schoolInfo,
		onConfirm: async (data) => {
			try {
				const res = await api.deleteSchool(data.scl_id);
				router.push({
					name: "admin.schools",
				});
				toastStore.openToastSuccess(res.data.message);
			} catch (e) {
				toastStore.openToastError(e.response.data.message);
			}
		},
	});
}

function handleNatureChange(value: number[]) {
	schoolChanges.value.nature = schoolNatures.value.filter((el) =>
		value.includes(el.tag_id),
	);
}

async function getSubscriptionInfo() {
	try {
		const originalChanged = changed.value;
		//
		// getSubscription returns expired subscriptions as well
		//
		// const res = await api.getSubscription(schoolId.value);
		// schoolInfo.value.subscription = res.data.data;
		// subscription.value = { ...res.data.data };
		// schoolChanges.value.subscription = { ...res.data.data };
		getSchoolInfo();
		if (!originalChanged) {
			nextTick(() => {
				changed.value = false;
			});
		}
	} catch {}
}

const toastStore = useToastStore();

const apiLoading = ref(false);
async function saveSchoolInfo() {
	const data: Partial<TEditSchoolParams> = {};
	if (schoolChanges.value.scl_name !== schoolInfo.value.scl_name) {
		data.scl_name = schoolChanges.value.scl_name;
	}
	if (schoolChanges.value.scl_address !== schoolInfo.value.scl_address) {
		data.scl_address = schoolChanges.value.scl_address;
	}
	if (
		schoolChanges.value.scl_contactname !== schoolInfo.value.scl_contactname
	) {
		data.scl_contactname = schoolChanges.value.scl_contactname;
	}
	if (schoolChanges.value.scl_phone !== schoolInfo.value.scl_phone) {
		data.scl_phone = schoolChanges.value.scl_phone;
	}
	if (schoolChanges.value.scl_email !== schoolInfo.value.scl_email) {
		data.scl_email = schoolChanges.value.scl_email;
	}
	if (schoolChanges.value.nature !== schoolInfo.value.nature) {
		data.school_nature = JSON.stringify(
			schoolChanges.value.nature.map((el) => el.tag_id),
		);
	}

	const data_keys = Object.keys(data);
	if (data_keys.length === 0) {
		return;
	}

	const res = new FormData();
	for (const key of data_keys) {
		res.append(key, data[key]);
	}
	try {
		apiLoading.value = true;
		const response = await api.editSchool(schoolId.value, res);
		toastStore.openToastSuccess(response.data.message);
		getSchoolInfo();
	} catch (e) {
		if (e.response) {
			toastStore.openToastError(e.response.data.message);
		} else {
			toastStore.openToastError(e.message);
		}
	} finally {
		apiLoading.value = false;
	}
}

onActivated(() => {
	getSchoolInfo();
	getNatures();
});
onMounted(() => {
	getSchoolInfo();
	getNatures();
});
</script>
<template>
	<div id="school-info-wrap">
		<p-button
			outlined
			as="router-link"
			:to="{
				name: 'admin.schools',
			}"
			:pt="{
				root: {
					class: 'admin-back-wrap',
				},
			}"
		>
			<i-ic-chevron-left />
			<span>{{ $t("app.base.back") }}</span>
		</p-button>
		<div class="general-info">
			<div id="school-billing-info">
				<h2>{{ $t("app.school.billingInfo") }}</h2>
				<p-float-label>
					<label for="add-school-name">{{ $t("app.base.name") }}</label>
					<p-input-text
						id="add-school-name"
						v-model="schoolChanges.scl_name"
					></p-input-text>
				</p-float-label>
				<p-float-label>
					<label for="add-school-address">
						{{ $t("app.base.address") }}
					</label>
					<p-input-text
						id="add-school-address"
						v-model="schoolChanges.scl_address"
					></p-input-text>
				</p-float-label>
				<p-float-label>
					<label for="add-school-contact-person">
						{{ $t("app.school.contactPerson") }}
					</label>
					<p-input-text
						id="add-school-contact-person"
						v-model="schoolChanges.scl_contactname"
					></p-input-text>
				</p-float-label>
				<p-float-label>
					<label for="add-school-contact-person">
						{{ $t("app.base.phone", 2) }}
					</label>
					<p-input-text
						id="add-school-contact-person"
						v-model="schoolChanges.scl_phone"
					></p-input-text>
				</p-float-label>
				<p-float-label>
					<label for="add-school-contact-person">
						{{ $t("app.auth.email") }}
					</label>
					<p-input-text
						id="add-school-contact-person"
						v-model="schoolChanges.scl_email"
					></p-input-text>
				</p-float-label>
			</div>
			<div id="school-general-info">
				<h2>{{ $t("app.school.generalInfo") }}</h2>
				<div>
					<p-button
						severity="danger"
						@click="openDeleteModal"
					>
						{{
							(
								$t("app.base.delete") +
								" " +
								$t("app.base.school")
							).toUpperCase()
						}}
					</p-button>
				</div>
				<p-multi-select
					:default-value="schoolChanges.nature?.map((el) => el.tag_id)"
					mode="tags"
					filter
					auto-filter-focus
					:options="schoolNatures"
					option-label="tag_name"
					class="max-w-[400px]"
					display="chip"
					:placeholder="
						$t('app.tags.' + TAG_TYPES.SCHOOL_TYPE.toLowerCase(), 2)
					"
					:max-selected-labels="3"
					:selected-items-label="
						$t('app.base.selectedItems', {
							count: schoolChanges.nature?.length || 0,
						})
					"
					option-value="tag_id"
					:show-toggle-all="false"
					@filter="tagSearch = $event.value"
					@value-change="handleNatureChange"
				>
					<template #header>
						<div class="dropdown-header-text">
							{{
								$t(
									"app.base.pleaseSearch",
									{
										item: $t(
											"app.tags." + TAG_TYPES.SCHOOL_TYPE.toLowerCase(),
											2,
										),
									},
									3,
								)
							}}
						</div>
					</template>
				</p-multi-select>
				<template v-if="subscription">
					<subscription-comp
						:subscription="subscription"
						:deletable="can('remove-school-subscription', 'admin')"
						:editable="can('edit-school-subscription', 'admin')"
						class="subscription"
						:scl-id="schoolId"
						:action="schoolInfo.subscription ? 'edit' : 'add'"
						@delete="subscription = null"
						@refresh="getSubscriptionInfo"
					/>
				</template>
				<template v-else>
					<p>{{ $t("app.admin.school.notSubscribed") }}</p>
					<div>
						<p-button @click="addEmptySubscription">
							{{
								$t("app.base.add", {
									item: $t("app.subscription.subscription"),
								})
							}}
						</p-button>
					</div>
				</template>
				<span v-if="schoolInfo?.scl_created">
					{{ $t("app.admin.school.createdAt") }}: {{ schoolInfo?.scl_created }}
				</span>

				<span>
					{{ $t("app.admin.school.updatedAt") }}: {{ schoolInfo?.scl_updated }}
				</span>
			</div>
		</div>
		<div
			v-if="changed"
			class="controller-wrap"
		>
			<p-button
				severity="danger"
				@click="setupFields"
			>
				<template #icon><i-ic-baseline-restart-alt class="large" /></template>
			</p-button>
			<p-button
				class="wide-button"
				:disabled="apiLoading"
				@click="saveSchoolInfo"
			>
				<template #icon><i-ic-baseline-save class="large" /></template>
			</p-button>
		</div>
		<div
			v-if="schoolInfo"
			class="admins"
		>
			<view-school-admins
				:scl-id="schoolId"
				:scl-name="schoolInfo.scl_name"
				:administrators="schoolInfo.administrators"
				@refresh="getSchoolInfo"
			/>
		</div>
		<div
			v-if="schoolInfo"
			class="locations"
		>
			<view-school-locations
				:scl-id="schoolId"
				:scl-name="schoolInfo.scl_name"
				:locations="schoolInfo.locations"
				@refresh="getSchoolInfo"
			/>
		</div>
	</div>
</template>
<style scoped lang="scss">
#school-info-wrap {
	padding: 1rem 3rem 3rem;
	.general-info {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		#school-billing-info,
		#school-general-info {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 25px;
			input {
				display: block;
				width: 70%;
			}
			h2 {
				font-size: 18px;
				font-weight: 700;
			}
		}
	}
	.admins,
	.locations {
		margin: 2rem 0;
	}
	.subscription {
		margin: 1rem 0;
	}
	.controller-wrap {
		display: flex;
		gap: 10px;
		margin: 20px 0;
	}
}
</style>
